import React from 'react';
import {
    Container,
    Paper,
    Button,
    Typography,
    Box,
    styled,
    alpha,
    Divider,
} from '@mui/material';
import {
    Description,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const BackgroundContainer = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    background: theme.palette.mode === 'dark'
        ? `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.04) 0%, transparent 50%),
      linear-gradient(135deg, ${theme.palette.background.default} 0%, ${theme.palette.background.paper} 100%)
    `
        : `
      radial-gradient(circle at 20% 80%, rgba(102, 126, 234, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(118, 75, 162, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 40% 40%, rgba(102, 126, 234, 0.05) 0%, transparent 50%),
      linear-gradient(135deg, #FAFBFC 0%, #F4F6F8 100%)
    `,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
}));

const StyledPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(4),
    borderRadius: 16,
    boxShadow: theme.shadows[8],
    backdropFilter: 'blur(10px)',
    background: alpha(theme.palette.background.paper, 0.9),
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    maxWidth: 800,
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto',
}));

const StyledButton = styled(Button)(({ theme }) => ({
    borderRadius: 12,
    textTransform: 'none',
    fontWeight: 600,
    padding: theme.spacing(1.5),
    fontSize: '1rem',
}));

const TermsOfUse: React.FC = () => {
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();

    const handleBack = () => {
        // Se veio do SignUpForm, volta para o login com a aba de cadastro ativa e os dados
        if (location.state?.fromSignUp) {
            navigate('/login', { 
                state: { 
                    showSignUp: true,
                    formData: location.state.formData
                } 
            });
        } else {
            // Caso contrário, volta para a página anterior
            navigate(-1);
        }
    };

    return (
        <BackgroundContainer>
            <Container maxWidth="md">
                <StyledPaper elevation={3}>
                    <Box mb={3}>
                        
                        <Box display="flex" alignItems="center" mb={2}>
                            <Description sx={{ mr: 2, color: 'primary.main' }} />
                            <Typography variant="h4" component="h1" fontWeight="bold">
                                Termos de Uso
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ mb: 3 }} />

					<Box sx={{ '& > *': { mb: 3 } }}>
						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
								Termos de Uso da Plataforma LIATALK AI
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								GAIA PLUS AI LTDA, CNPJ 57.986.228/0001-10, com sede na Av. Paulista, 807, Conj. 1614, Bela Vista, São Paulo/SP – CEP 01311-915, é responsável pela operação da plataforma tecnológica destinada à criação e gestão de agentes de inteligência artificial (“Plataforma” ou “Liatalk AI”).
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								Ao criar uma conta, acessar ou utilizar a Plataforma, você (“Usuário”) declara ter lido e concordado com estes Termos de Uso (“Termos”).
							</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
								1. Quem somos e como funciona
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								A Liatalk AI disponibiliza um ambiente no-code/low-code para configuração de agentes de IA e integrações com serviços de terceiros. As credenciais de acesso são pessoais e intransferíveis.
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								Estes Termos devem ser lidos em conjunto com a Política de Privacidade da Liatalk AI.
							</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
								2. Aceite dos Termos
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								O aceite ocorre eletronicamente no momento do cadastro ou do primeiro uso. O aceite eletrônico possui validade jurídica equivalente à assinatura física, conforme a legislação brasileira.
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								Podemos atualizar estes Termos a qualquer tempo. A versão vigente será sempre a publicada no site/app da Liatalk AI. O uso após a atualização representa concordância com as alterações, observados os deveres de informação e transparência nas relações de consumo.
							</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
								3. Cadastro, conta e segurança
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>3.1. O Usuário deve fornecer dados verdadeiros e mantê-los atualizados.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>3.2. Menores ou incapazes somente poderão utilizar a Plataforma com consentimento expresso do responsável legal, mediante comprovação documental quando solicitado.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>3.3. O Usuário é responsável por todas as ações realizadas mediante suas credenciais e deve nos informar imediatamente qualquer suspeita de acesso indevido.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>3.4. É vedado vender, ceder, alugar ou transferir a conta.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>3.5. A Liatalk AI pode solicitar informações adicionais para verificação de identidade e prevenção a fraudes.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>3.6. Contas inativas por mais de 6 meses poderão ser encerradas mediante aviso prévio ao usuário, mantendo a confidencialidade das credenciais.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
								4. Equipes e espaços de trabalho
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								A conta pode permitir a inclusão de usuários adicionais em um Workspace. A Liatalk AI poderá auditar o uso para verificar conformidade com estes Termos e com os limites do plano contratado, podendo suspender contas em caso de violação. O administrador do workspace será responsável pelos atos praticados pelos membros que convidar.
							</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">
								5. Planos, preços, pagamentos e renovação
							</Typography>
							<Typography variant="subtitle1" gutterBottom fontWeight="bold">5.1. Planos disponíveis</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								Mantemos três planos, conforme tabela comercial vigente:
							</Typography>
							<Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
								<li>
									<Typography variant="body2" color="text.secondary">
										<strong>GRÁTIS</strong><br />
										Preço: R$ 0,00<br />
										• Agentes de IA: ilimitado<br />
										• Créditos/mês: 250 por até 3 meses<br />
										• CRM — Usuários incluídos: 3<br />
										• Whitelabel: —<br />
										• Validade do plano: 3 meses (após esse período, o Usuário poderá migrar para plano pago para continuidade dos serviços).
									</Typography>
								</li>
								<li>
									<Typography variant="body2" color="text.secondary">
										<strong>Upgrade</strong><br />
										Preço: R$ 299,00/mês<br />
										• Agentes de IA: ilimitado<br />
										• Créditos/mês: 5.000<br />
										• CRM — Usuários incluídos: 10<br />
										• Whitelabel: —<br />
										• Vigência contratual: 12 meses (renovação automática, salvo cancelamento).
									</Typography>
								</li>
								<li>
									<Typography variant="body2" color="text.secondary">
										<strong>Liatalk AI PRO – WHITELABEL</strong><br />
										Preço: R$ 799,00/mês<br />
										• Agentes de IA: ilimitado<br />
										• Créditos/mês: 20.000<br />
										• CRM — Usuários incluídos: 20<br />
										• Whitelabel: 1 empresa incluída<br />
										• Vigência contratual: 12 meses (renovação automática, salvo cancelamento).
									</Typography>
								</li>
							</Box>
							<Typography variant="subtitle2" gutterBottom fontWeight="bold">Add-ons (disponíveis em qualquer plano):</Typography>
							<Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
								<li>
									<Typography variant="body2" color="text.secondary">
										Créditos adicionais (1.000 créditos): Planos pagos (Upgrade e Liatalk AI PRO – WHITELABEL): R$ 99,99 • Plano GRÁTIS: R$ 200,00. São opcionais (compra avulsa, sem recorrência), acumulativos até o consumo integral e não reembolsáveis (ver item 15.4).
									</Typography>
								</li>
								<li>
									<Typography variant="body2" color="text.secondary">
										Usuários adicionais do CRM: R$ 150,00/usuário/mês no GRÁTIS e R$ 70,00/usuário/mês nos planos pagos (cobrança mensal recorrente enquanto ativos).
									</Typography>
								</li>
								<li>
									<Typography variant="body2" color="text.secondary">Suporte Premium / Integrações: sob orçamento.</Typography>
								</li>
							</Box>
							<Typography variant="body2" color="text.secondary" paragraph>
								Observação comercial: o CRM Liatalk AI é igual para todos os planos; o que varia é a quantidade de usuários, agentes, créditos e o nível de suporte.
							</Typography>

							<Typography variant="subtitle1" gutterBottom fontWeight="bold">5.2. Contratação</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								A contratação ocorre mediante aceite destes Termos e pagamento no checkout, com informação clara de preço total, características do serviço e condições de cancelamento.
							</Typography>

							<Typography variant="subtitle1" gutterBottom fontWeight="bold">5.3. Pagamentos e recorrência</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Os planos são cobrados mensalmente por cartão de crédito ou PIX (intermediadores parceiros).</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Usuários adicionais do CRM possuem cobrança mensal recorrente, faturados juntamente com a renovação do plano enquanto permanecerem ativos.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Em caso de insucesso na cobrança, a conta e/ou os recursos adicionais poderão ser suspensos até a regularização.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Mês iniciado. O cancelamento solicitado após o início do ciclo mensal não gera reembolso do mês em curso; apenas impede novas renovações, ressalvado o direito de arrependimento de 7 (sete) dias, quando aplicável.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Haverá aviso prévio de renovação automática com antecedência mínima de 5 dias e reajuste anual de preços conforme IPCA.</Typography>

							<Typography variant="subtitle1" gutterBottom fontWeight="bold">5.4. Créditos do plano × créditos adicionais</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>(i) Créditos do plano (franquia mensal): não acumuláveis; o saldo zera ao final de cada ciclo mensal.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>(ii) Créditos adicionais (compra avulsa, sem recorrência): acumulativos até o consumo integral; não reembolsáveis (ver item 15.4).</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Ordem de consumo: primeiro a franquia mensal do plano; depois os créditos adicionais.</Typography>

							<Typography variant="subtitle1" gutterBottom fontWeight="bold">5.5. Trial/gratuidade</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Podemos oferecer 7 (sete) dias de teste gratuito por CPF/CNPJ/e-mail/organização. Ao final, o acesso é interrompido se não houver contratação. Funcionalidades e limites podem ser reduzidos no Trial. Podemos revogar o Trial em caso de abuso, fraude ou violação destes Termos.</Typography>

							<Typography variant="subtitle1" gutterBottom fontWeight="bold">5.6. Reajustes e alteração de preços</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>A Liatalk AI poderá ajustar preços e condições comerciais a qualquer tempo para novas contratações e novas compras de add-ons, mediante divulgação no site/checkout.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Para assinaturas em curso com renovação automática, o novo preço passará a valer na renovação seguinte, mediante aviso prévio claro e razoável, assegurado ao Usuário o direito de cancelar antes da renovação.</Typography>

							<Typography variant="subtitle1" gutterBottom fontWeight="bold">5.7. Notas fiscais</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Emitidas no mês da aquisição e enviadas por e-mail; se não receber, o Usuário poderá solicitar ao suporte.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">6. Uso permitido e vedações</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>6.1. O Usuário deve utilizar a Plataforma de acordo com a lei e com estes Termos.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>6.2. É proibido, entre outros: (i) violar direitos de terceiros; (ii) burlar limites de uso, fraudar planos ou meios de pagamento; (iii) engenharia reversa/varreduras sem autorização; (iv) coletar dados sem base legal; (v) publicar/armazenar conteúdo ilícito; (vi) usar robôs para extração indevida de dados/overload; (vii) compartilhar credenciais.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>6.3. A violação poderá resultar em advertência, suspensão, cancelamento e medidas cabíveis.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>6.4. O Usuário compromete-se a utilizar a IA de forma ética, abstendo-se de gerar conteúdo ilegal, discriminatório, difamatório ou fraudulento.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">7. Serviços e integrações de terceiros</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>7.1. A Liatalk AI pode integrar-se a provedores (ex.: LLMs, WhatsApp Business). Cada serviço possui termos próprios que o Usuário deve aceitar antes de usar.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>7.2. O uso desses serviços é de responsabilidade do Usuário; a Liatalk AI não responde por indisponibilidades, mudanças de política ou danos desses terceiros.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>7.3. O Usuário deverá ler e concordar com os termos e políticas dos serviços de terceiros integrados à Liatalk AI (como OpenAI, Meta ou Google Cloud).</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">8. Propriedade intelectual</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>8.1. Marcas, logotipos, interfaces, código-fonte, bancos de dados e demais ativos pertencem à Liatalk AI ou licenciadores.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>8.2. Concedemos licença limitada, revogável, não exclusiva e intransferível para uso da Plataforma conforme estes Termos.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>8.3. É proibido copiar, distribuir, sublicenciar, criar obras derivadas ou explorar comercialmente a Plataforma sem autorização expressa e escrita.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>8.4. Os agentes e conteúdos criados pelo Usuário pertencem a ele, exceto os modelos e componentes proprietários da Liatalk AI.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">9. Conteúdo do Usuário</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>9.1. O Usuário é o único responsável pelo conteúdo inserido, enviado, treinado ou processado na Plataforma.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>9.2. O Usuário declara possuir os direitos necessários e autoriza o tratamento estritamente necessário para operar a Plataforma (processamento, hospedagem, backup e exibição nos limites das funcionalidades contratadas).</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>9.3. A Liatalk AI poderá remover conteúdos ilegais, ofensivos ou que violem direitos de terceiros, sem prejuízo das medidas cabíveis.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">10. Proteção de dados e privacidade</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>10.1. A Liatalk AI trata dados pessoais conforme a LGPD e sua Política de Privacidade.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>10.2. Usaremos dados pessoais para operar a Plataforma, cumprir obrigações legais, prevenir fraudes e melhorar serviços, observando os princípios de finalidade, adequação e necessidade e os direitos dos titulares.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>10.3. O Usuário poderá exercer seus direitos (acesso, correção, portabilidade, eliminação etc.) pelos canais indicados.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>10.4. Quando a funcionalidade envolver provedores de IA, os dados enviados poderão ser processados por esses provedores conforme suas políticas.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>10.5. Observam-se também os princípios do Marco Civil da Internet relativos a privacidade e registros.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>10.6. Encarregado de Dados (DPO): Bruno Almeida – dpo@liatalk.ai. Consulte também nossa Política de Privacidade no site oficial.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">11. Garantias, isenções e limitações de responsabilidade</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>11.1. A Plataforma é fornecida “no estado em que se encontra” e “conforme disponível”. Não garantimos funcionamento ininterrupto, livre de erros ou compatível com todos os ambientes.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>11.2. A Liatalk AI não se responsabiliza por: (i) falhas de internet, nuvem ou integrações de terceiros; (ii) danos de malware/ataques ou eventos fora do nosso controle; (iii) decisões tomadas com base em resultados de agentes de IA; (iv) links/conteúdos de terceiros; (v) custos de dados/telefonia/SMS.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>11.3. Na extensão máxima permitida por lei, a responsabilidade total da Liatalk AI por danos diretos fica limitada ao montante efetivamente pago nos 12 meses anteriores ao evento. Danos indiretos (lucros cessantes, perda de receita, danos morais etc.) ficam excluídos, salvo dolo.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								11.4. Natureza dos resultados de IA e responsabilidade pelas instruções. A Plataforma disponibiliza modelos e integrações de inteligência artificial de caráter probabilístico, cujo desempenho e saída variam conforme dados, contexto e instruções (prompts) do Usuário. Cabe exclusivamente ao Usuário revisar, validar e aprovar quaisquer conteúdos, decisões, preços, valores, descontos, ofertas ou comunicações gerados por agentes de IA configurados na sua conta.
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>11.5. Na extensão máxima permitida em lei, a Liatalk AI não se responsabiliza por perdas, danos, autuações ou violações decorrentes de erros, imprecisões, omissões, vieses ou “alucinações” de IA, nem por atos praticados com base nesses resultados, cabendo ao Usuário implementar revisões humanas e controles de negócio adequados.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>11.6. Os resultados fornecidos pela IA têm caráter informativo e não substituem aconselhamento profissional humano.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">12. Suspensão, encerramento e inativação</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>12.1. Podemos suspender ou encerrar o acesso em caso de descumprimento destes Termos, ordem judicial, risco à segurança, fraude ou inadimplência.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>12.2. O Usuário pode solicitar a inativação da conta a qualquer tempo; obrigações vencidas permanecem devidas.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>12.3. O encerramento definitivo da conta será precedido de notificação prévia, salvo em casos de fraude ou violação grave destes Termos.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">13. Comunicações e suporte</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>13.1. As comunicações oficiais podem ocorrer pela própria Plataforma ou por e-mail.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>13.2. Contatos: bruno@liatalk.ai.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>13.3. O Usuário concorda que os dados de contato atualizados são válidos para notificações.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">14. SAC e atendimento ao consumidor</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>Mantemos SAC gratuito, com canais informados de forma clara, observando as diretrizes aplicáveis de atendimento ao consumidor.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">15. Cancelamento, reembolso e créditos</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>15.1. Direito de arrependimento. Contratações realizadas fora do estabelecimento (internet) podem ser desfeitas em até 7 (sete) dias corridos a contar da adesão ou do primeiro acesso/recebimento do serviço, com reembolso integral pelo mesmo meio de pagamento utilizado.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								15.2. Após o prazo legal de 7 dias
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								a) Planos mensais (recorrência mês a mês): (i) O cancelamento pode ser solicitado a qualquer tempo; não há reembolso do ciclo mensal vigente se o pedido ocorrer após o início do mês; a renovação/cobrança futura é cessada a partir do protocolo do pedido.
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								b) Planos anuais pré-pagos: reembolso = total pago menos: meses já utilizados pro rata die (incluindo o mês corrente proporcional); 20% sobre o total contratado (taxa administrativa/desmobilização); e custos de terceiros/tributos não reembolsáveis (quando houver).
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>15.3. Créditos do plano (franquia mensal): não acumuláveis; zera ao final de cada ciclo.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>
								15.4. Créditos adicionais (compra avulsa, sem recorrência): (a) Não reembolsáveis; (b) Acumulam até o consumo integral; (c) Ordem de consumo: franquia mensal → créditos adicionais; (d) Em cancelamento/inativação, permanecem congelados e só podem ser usados mediante reativação de um plano elegível.
							</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>15.5. Indisponibilidade imputável à Liatalk AI: interrupção contínua superior a 24h ou acumulada superior a 48h dentro do ciclo dará direito a crédito proporcional ou reembolso parcial.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>15.6. Encerramento pela Liatalk AI sem culpa do Usuário: devolveremos valores adiantados relativos ao período não utilizado.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>15.7. Reembolsos serão processados em até 10 dias úteis.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>15.8. Como cancelar: pelo painel da Plataforma ou e-mail bruno@liatalk.ai.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>15.9. Reembolsos serão realizados pelo mesmo meio de pagamento utilizado pelo Usuário.</Typography>
						</Box>

						<Box>
							<Typography variant="h6" gutterBottom fontWeight="bold" color="primary">16. Disposições finais</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>16.1. A eventual nulidade de uma cláusula não invalida as demais.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>16.2. A tolerância quanto ao descumprimento de obrigações não importa renúncia de direitos.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>16.3. Em caso de reorganização societária/sucessão, estes Termos permanecem válidos.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>16.4. As partes comprometem-se a buscar solução amigável antes de recorrer ao Poder Judiciário.</Typography>
							<Typography variant="body2" color="text.secondary" paragraph>16.5. Este instrumento é regido pelas leis brasileiras e é eleito o foro da Comarca de São Paulo/SP.</Typography>
						</Box>
					</Box>

                    <Divider sx={{ my: 3 }} />

                    <Box textAlign="center">
                        <StyledButton
                            variant="contained"
                            sx={{ color: theme.palette.mode === 'dark' ? '#000' : undefined }}
                            onClick={handleBack}
                            size="large"
                        >
                            Voltar ao Cadastro
                        </StyledButton>
                    </Box>
                </StyledPaper>
            </Container>
        </BackgroundContainer>
    );
};

export default TermsOfUse;
